import { ZONE_COLOURS } from "../../helpers/const";
import type { ZoneLabel } from "../../types/graphql";
import type { ZonePerf, ZoneTypePerf } from "./types";

/** Whole numbers with thousands separators. */
export const fmtInt = (n: number): string => Math.round(n).toLocaleString("en-GB");

export const hexToRgb = (hex: string): [number, number, number] => {
  const h = (hex || "#888888").replace("#", "");
  return [
    Number.parseInt(h.slice(0, 2), 16),
    Number.parseInt(h.slice(2, 4), 16),
    Number.parseInt(h.slice(4, 6), 16),
  ];
};

const luminance = ([r, g, b]: [number, number, number]): number =>
  (0.299 * r + 0.587 * g + 0.114 * b) / 255;

export const rgba = ([r, g, b]: [number, number, number], alpha: number): string =>
  `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(2)})`;

/** The colour a zone is named for (shared with the members table chips). */
export const zoneHex = (zone: ZoneLabel): string => ZONE_COLOURS.get(zone) ?? "#888888";
export const zoneRgb = (zone: ZoneLabel): [number, number, number] => hexToRgb(zoneHex(zone));
/** Whether a zone's colour is dark enough that white text reads better on it. */
export const zoneIsDark = (zone: ZoneLabel): boolean => luminance(zoneRgb(zone)) < 0.55;

/**
 * The metrics the matrix can shade by — the same set as the member tools' Zone
 * Performance heatmap.
 *   - `fn` reads one event-type cell; `total` reads a zone's overall figure.
 *   - `bad` metrics are worse when higher (the "leader" is the worst).
 */
export interface Metric {
  key: string;
  label: string;
  bad?: boolean;
  fn: (t: ZoneTypePerf) => number;
  fmt: (v: number) => string;
  total: (z: ZonePerf) => number;
}

const one = (v: number): string => v.toFixed(1);
const pct = (v: number): string => `${Math.round(v)}%`;
const whole = (v: number): string => fmtInt(v);

export const METRICS: Metric[] = [
  {
    key: "avg_show",
    label: "avg attendance / event",
    fn: (t) => (t.events ? t.showed / t.events : 0),
    fmt: one,
    total: (z) => (z.totals.events ? z.totals.showed / z.totals.events : 0),
  },
  {
    key: "avg_rsvp",
    label: "avg RSVPs / event",
    fn: (t) => (t.events ? t.rsvps / t.events : 0),
    fmt: one,
    total: (z) => (z.totals.events ? z.totals.rsvps / z.totals.events : 0),
  },
  {
    key: "showed",
    label: "total attendance",
    fn: (t) => t.showed,
    fmt: whole,
    total: (z) => z.totals.showed,
  },
  {
    key: "events",
    label: "number of events",
    fn: (t) => t.events,
    fmt: whole,
    total: (z) => z.totals.events,
  },
  {
    key: "rate",
    label: "show-up rate",
    fn: (t) => t.showUpRate,
    fmt: pct,
    total: (z) => z.totals.showUpRate,
  },
  {
    key: "no_show",
    label: "events with no attendance",
    bad: true,
    fn: (t) => t.noShowEvents,
    fmt: whole,
    total: (z) => z.types.reduce((n, t) => n + (t.noShowEvents || 0), 0),
  },
];

export const METRIC_BY_KEY = new Map(METRICS.map((m) => [m.key, m]));

/** A small ▲▼ delta chip string, e.g. "▲ 4" / "▼ 2 pt". */
export const deltaText = (
  cur: number,
  prev: number,
  unit = "",
): { text: string; up: boolean } | null => {
  const d = Math.round((cur - prev) * 10) / 10;
  if (!d) {
    return null;
  }
  const arrow = d > 0 ? "▲" : "▼";
  return { text: `${arrow} ${Math.abs(d)}${unit ? ` ${unit}` : ""}`, up: d > 0 };
};

/** Colour for a show-up-rate badge. */
export const rateColour = (rate: number): string =>
  rate >= 60 ? "#00583D" : rate >= 30 ? "#b26a00" : "#A32D2D";

/** Local time-of-day band for a UTC minutes-of-day and a region offset (hours). */
export const bandOf = (
  utcMin: number,
  offsetHours: number,
): "morning" | "afternoon" | "evening" | "night" => {
  const m = ((Math.round(utcMin + offsetHours * 60) % 1440) + 1440) % 1440;
  const hr = Math.floor(m / 60);
  if (hr >= 6 && hr <= 11) {
    return "morning";
  }
  if (hr >= 12 && hr <= 17) {
    return "afternoon";
  }
  if (hr >= 18 && hr <= 22) {
    return "evening";
  }
  return "night";
};

/** An event's local date + time in a region (shift the UTC instant by the offset). */
export const localDateTime = (
  startMs: number,
  offsetHours: number,
): { day: string; time: string } => {
  const dt = new Date(startMs + offsetHours * 3600 * 1000);
  return {
    day: dt.toLocaleDateString("en-GB", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      timeZone: "UTC",
    }),
    time: `${String(dt.getUTCHours()).padStart(2, "0")}:${String(dt.getUTCMinutes()).padStart(2, "0")}`,
  };
};

export const TZ_BANDS = ["morning", "afternoon", "evening", "night"] as const;
export const BAND_LABEL: Record<string, string> = {
  morning: "Morning",
  afternoon: "Afternoon",
  evening: "Evening",
  night: "Night",
};
export const BAND_HOURS: Record<string, string> = {
  morning: "6–11",
  afternoon: "12–17",
  evening: "18–22",
  night: "23–5",
};
